import type { WeldTelemetrySampleRow } from './weldTelemetryClient'
import { convertWeldDataValue } from './weldUnitUtils'
import {
  applyPqrToleranceToTriple,
  type PqrLimitsMode
} from './pqr/applyPqrTolerance'

export interface ParamStats {
  min: number | null
  avg: number | null
  max: number | null
}

export const NULL_STATS: ParamStats = { min: null, avg: null, max: null }

/**
 * Column keys for which the Pass Analytics table renders a stacked
 * Min / Avg / Max summary in the column header. Order is informational; the
 * table preserves its own column order.
 */
export const STATS_COLUMN_KEYS = [
  'travelSpeed',
  'volts',
  'amps',
  'wireIpm',
  'widthMil',
  'targetVlt',
  'csBias',
  'bpm',
  'totWireIn',
  'trueEnergy',
  'heat'
] as const

export type StatsColumnKey = (typeof STATS_COLUMN_KEYS)[number]

/**
 * Read the unit-converted numeric value for a stats column from a sample.
 * Mapping mirrors PassAnalyticsTable cell renderers so the header stats and
 * the cells always show numbers that came from the same conversion path:
 *   - `heat`: prefer `rprtHeat`, fall back to `calcHeat`, convert as `rprtHeat`.
 *   - everything else: read the matching field on the sample, convert with
 *     the column key directly.
 */
function readValue(
  sample: WeldTelemetrySampleRow,
  columnKey: string
): number | null {
  if (columnKey === 'heat') {
    const raw = sample.rprtHeat ?? sample.calcHeat
    return convertWeldDataValue(raw ?? null, 'rprtHeat')
  }
  const raw = (sample as unknown as Record<string, unknown>)[columnKey]
  const num = typeof raw === 'number' ? raw : null
  return convertWeldDataValue(num, columnKey)
}

/**
 * Compute Min / Avg / Max for one column over the given samples.
 *
 * Null / non-finite values are skipped. Returns `{null, null, null}` when no
 * sample has a finite value for that column (or `samples` is empty).
 */
export function computeStats(
  samples: WeldTelemetrySampleRow[],
  columnKey: string
): ParamStats {
  if (samples.length === 0) return NULL_STATS
  let min = Infinity
  let max = -Infinity
  let sum = 0
  let count = 0
  for (const sample of samples) {
    const value = readValue(sample, columnKey)
    if (value === null || !Number.isFinite(value)) continue
    if (value < min) min = value
    if (value > max) max = value
    sum += value
    count += 1
  }
  if (count === 0) return NULL_STATS
  return {
    min,
    max,
    avg: sum / count
  }
}

/**
 * Compute Min / Avg / Max for every stats column. Returned object has
 * `STATS_COLUMN_KEYS` as keys; consumers can index it by column key.
 */
export function computeAllStats(
  samples: WeldTelemetrySampleRow[]
): Record<StatsColumnKey, ParamStats> {
  const out = {} as Record<StatsColumnKey, ParamStats>
  for (const key of STATS_COLUMN_KEYS) {
    out[key] = computeStats(samples, key)
  }
  return out
}

/**
 * Apply saved Welding Parameters tolerance to every stats-column triple for
 * Pass Analytics display (header, Recalculate dialog). Raw stats are unchanged.
 */
export function applyToleranceToAllStats(
  stats: Record<StatsColumnKey, ParamStats>,
  boundary: number,
  limitsMode: PqrLimitsMode = 'minmax'
): Record<StatsColumnKey, ParamStats> {
  if (boundary === 0) return { ...stats }
  const out = {} as Record<StatsColumnKey, ParamStats>
  for (const key of STATS_COLUMN_KEYS) {
    out[key] = applyPqrToleranceToTriple(stats[key] ?? NULL_STATS, boundary, limitsMode)
  }
  return out
}

export type { PqrLimitsMode }
